import { useEffect, useMemo, useRef, useState } from "react";
import { fileURL } from "../../../utils/fileURL";
import { validarArchivo, acceptDe } from "../../../utils/limitesArchivos";

function BrandFormModal({ isOpen, onClose, brandData, onSubmit }) {
  const isEdit = !!brandData;

  const initialForm = useMemo(
    () => ({
      nombre: brandData?.nombre || "",
      estado: brandData?.estado ?? 1,
    }),
    [brandData]
  );

  const [form, setForm] = useState(initialForm);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const objectUrlRef = useRef(null);

  // ✅ alert del modal
  const [alert, setAlert] = useState(null); // { type, message }
  const alertTimerRef = useRef(null);

  // ✅ loading para evitar doble submit
  const [saving, setSaving] = useState(false);

  const showAlert = (type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4000);
  };

  // ✅ reset y preview al abrir / cambiar brand
  useEffect(() => {
    if (!isOpen) return;

    setForm(initialForm);
    setSaving(false);
    setAlert(null);
    setImageFile(null);

    // limpiar objectURL anterior
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    if (brandData?.url_image) setImagePreview(fileURL(brandData.url_image));
    else setImagePreview(null);

    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, [isOpen, initialForm, brandData]);

  // ✅ ESC para cerrar
  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // ✅ cleanup objectURL al desmontar
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  const updateField = (e) => {
    const { name, value } = e.target;

    // estado como número
    if (name === "estado") {
      setForm((p) => ({ ...p, [name]: Number(value) }));
      return;
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleImage = async (e) => {
    // Se guarda antes del await: mirar el contenido del archivo es asincronico
    // y despues del corte el handler ya no es dueno del evento.
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;

    const rechazo = await validarArchivo(file, "imagen_marca");
    if (rechazo) {
      showAlert("error", rechazo);
      // Sin esto, volver a elegir el mismo archivo no dispara onChange.
      input.value = "";
      return;
    }

    setImageFile(file);

    // limpiar objectURL anterior
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);

    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setImagePreview(url);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    setAlert(null);

    const cleanName = String(form.nombre || "").trim();
    if (!cleanName) {
      showAlert("error", "Brand name is required.");
      return;
    }

    const fd = new FormData();
    fd.append("nombre", cleanName);
    fd.append("estado", String(Number(form.estado))); // backend friendly
    if (imageFile) fd.append("imagen_marca", imageFile);

    setSaving(true);

    try {
      await onSubmit(fd); // si falla, debe lanzar error con message limpio
      onClose?.();
    } catch (error) {
      console.error("Brand save failed:", error);
      showAlert("error", error?.message || "Could not save brand.");
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={ui.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div style={ui.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={ui.header}>
          <h2 style={ui.title}>{isEdit ? "Edit Brand" : "Create Brand"}</h2>
          <button style={ui.closeBtn} onClick={onClose} disabled={saving} title="Close">
            ×
          </button>
        </div>

        {/* ✅ ALERT */}
        {alert && (
          <div
            style={{
              ...ui.alert,
              ...(alert.type === "error" ? ui.alertError : ui.alertInfo),
            }}
          >
            <span>{alert.message}</span>
            <button style={ui.alertX} onClick={() => setAlert(null)} disabled={saving}>
              ✕
            </button>
          </div>
        )}

        <form onSubmit={submit} style={ui.form}>
          <div style={ui.inputGroup}>
            <label style={ui.label}>Brand Name</label>
            <input
              type="text"
              name="nombre"
              value={form.nombre}
              onChange={updateField}
              style={ui.input}
              required
              disabled={saving}
              autoFocus
            />
          </div>

          <div style={ui.inputGroup}>
            <label style={ui.label}>Status</label>
            <select
              name="estado"
              value={Number(form.estado)}
              onChange={updateField}
              style={ui.input}
              disabled={saving}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div style={ui.inputGroup}>
            <label style={ui.label}>Brand Image</label>

            <div
              style={{
                ...ui.uploadBox,
                ...(saving ? ui.uploadBoxDisabled : null),
              }}
              onClick={() => {
                if (saving) return;
                document.getElementById("brand-img-input")?.click();
              }}
              title="Click to upload brand image"
            >
              {imagePreview ? (
                <img src={imagePreview} alt="brand preview" style={ui.image} />
              ) : (
                <div style={ui.uploadPlaceholder}>Click to upload image</div>
              )}
            </div>

            <input
              id="brand-img-input"
              type="file"
              accept={acceptDe("imagen_marca")}
              onChange={handleImage}
              style={{ display: "none" }}
              disabled={saving}
            />
          </div>

          <div style={ui.footer}>
            <button type="button" onClick={onClose} style={ui.btnCancel} disabled={saving}>
              Cancel
            </button>

            <button
              type="submit"
              style={{ ...ui.btnPrimary, ...(saving ? ui.btnPrimaryDisabled : null) }}
              disabled={saving}
            >
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Brand"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ui = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: "20px",
  },
  modal: {
    width: "460px",
    background: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
  header: {
    padding: "18px 24px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#1f2937",
    margin: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "22px",
    color: "#6b7280",
    opacity: 0.9,
  },

  // ✅ Alert styles
  alert: {
    margin: "14px 22px 0",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    fontSize: "13px",
    fontWeight: 700,
  },
  alertError: {
    background: "#FEF2F2",
    borderColor: "#FECACA",
    color: "#7F1D1D",
  },
  alertInfo: {
    background: "#EFF6FF",
    borderColor: "#BFDBFE",
    color: "#1E3A8A",
  },
  alertX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "16px",
    opacity: 0.7,
  },

  form: {
    padding: "24px",
  },
  inputGroup: {
    marginBottom: "18px",
  },
  label: {
    fontSize: "14px",
    color: "#374151",
    marginBottom: "6px",
    display: "block",
    fontWeight: 700,
  },
  input: {
    width: "100%",
    padding: "11px 12px",
    fontSize: "15px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    background: "#f9fafb",
    boxSizing: "border-box",
    outline: "none",
  },
  uploadBox: {
    border: "2px dashed #cbd5e1",
    borderRadius: "12px",
    height: "160px",
    background: "#f8fafc",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  uploadBoxDisabled: {
    opacity: 0.8,
    cursor: "not-allowed",
  },
  uploadPlaceholder: {
    color: "#6b7280",
    fontSize: "14px",
    fontWeight: 700,
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  footer: {
    padding: "18px 24px",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
  },
  btnPrimary: {
    padding: "10px 18px",
    background: "#2563eb",
    color: "white",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: 800,
  },
  btnPrimaryDisabled: {
    opacity: 0.75,
    cursor: "not-allowed",
  },
  btnCancel: {
    padding: "10px 18px",
    background: "#e5e7eb",
    color: "#374151",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: 700,
  },
};

export default BrandFormModal;
